import axios from "axios";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "../../context/AuthContext";
import {
    Card,
    Row,
    Col,
    Typography,
    Tag,
    Image,
    Badge,
    Input,
    DatePicker,
    Space,
    Modal,
    Pagination,
    Breadcrumb,
    Select,
} from "antd";
import {
    FileOutlined,
    SoundOutlined,
    FilePdfOutlined,
    EditFilled,
    CalendarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import Announcement_image from "../../assests/speaker-icon.png";
import { Edit } from "lucide-react";
import EditAnnouncement from "./EditAnnouncement";
import PendingAnnouncements from "./PendingAnnouncements";

const { Title, Paragraph, Text } = Typography;
const { Search } = Input;

export default function HrAnnouncements() {
    const [announcements, setAnnouncements] = useState([]);
    const { authData, backUrl } = useContext(AuthContext);
    const isMobile = window.innerWidth < 768;

    const [editAnnouncement, setEditAnnouncement] = useState(false);

    const [titleFilter, setTitleFilter] = useState("");
    const [dateFilter, setDateFilter] = useState(null);
    const [displayTypeFilter, setDisplayTypeFilter] = useState("");

    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(6);

    const [editData, setEditData] = useState(null);

    const [showPending, setShowPending] = useState(false);

    const fetchAnnouncements = async () => {
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/announcements/?created_by_id=${authData.employee.id}`,
                { headers: { Authorization: `Bearer ${authData.access}` } }
            );
            setAnnouncements(res.data.results || []);
        } catch (err) {
            console.error("Error fetching announcements:", err);
        }
    };

    useEffect(() => {
        fetchAnnouncements();
    }, []);

    const isImageFile = (fileUrl) => {
        if (!fileUrl) return false;
        return /\.(jpg|jpeg|png|gif|bmp|webp)$/i.test(fileUrl);
    };

    const reversedAnnouncements = [...announcements].reverse();

    const filteredAnnouncements = reversedAnnouncements.filter((item) => {
        const matchTitle = item.title.toLowerCase().includes(titleFilter.toLowerCase());
        const matchDate = dateFilter
            ? dayjs(item.created_at).isSame(dateFilter, "day")
            : true;
        const matchDisplayType = displayTypeFilter
            ? item.display_type === displayTypeFilter
            : true;
        return matchTitle && matchDate && matchDisplayType;
    });

    const total = filteredAnnouncements.length;
    const paginatedAnnouncements = filteredAnnouncements.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const handleCardClick = (item) => {
        setSelectedAnnouncement(item);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedAnnouncement(null);
        setEditAnnouncement(false);
    };

    const handlePageChange = (page, newPageSize) => {
        setCurrentPage(page);
        if (newPageSize !== pageSize) {
            setPageSize(newPageSize);
            setCurrentPage(1);
        }
    };

    // Reusable display_type tag
    const renderDisplayTypeTag = (display_type) => (
        <p style={{ fontWeight: 'bolder' }}>
            {display_type === "FLEXIBLE" ? "Flexible" : "Standard"}
        </p>
    );

    // Reusable date range display
    const renderDateRange = (item) => {
        if (item.display_type === "FLEXIBLE" && item.start_date && item.end_date) {
            return (
                <Text type="secondary" style={{ fontSize: "12px" }}>
                    <CalendarOutlined style={{ marginRight: 4 }} />
                    {dayjs(item.start_date).format("DD/MM/YYYY")} →{" "}
                    {dayjs(item.end_date).format("DD/MM/YYYY")}
                </Text>
            );
        }
        return null;
    };

    return editAnnouncement ? (
        <div style={{ padding: "20px" }}>
            <EditAnnouncement
                editData={editData}
                onBack={async () => {
                    await setEditAnnouncement(false);
                    window.location.reload();
                }}
            />
        </div>
    ) : showPending ? (
        <>
            <PendingAnnouncements onBack={() => setShowPending(false)} />
        </>
    ) : (
        <div style={{ padding: "20px" }}>
            {/* Modal */}
            <Modal
                open={isModalVisible}
                onCancel={handleModalClose}
                footer={null}
                width={700}
                title={selectedAnnouncement?.title}
            >
                {selectedAnnouncement && (
                    <div>
                        <EditFilled
                            onClick={() => {
                                setEditAnnouncement(true);
                                setEditData(selectedAnnouncement);
                            }}
                            style={{
                                color: "#0e497b",
                                fontSize: "25px",
                                marginBottom: "20px",
                                cursor: "pointer",
                            }}
                        />

                        {selectedAnnouncement.announcement_file &&
                            (isImageFile(selectedAnnouncement.announcement_file) ? (
                                <Image
                                    src={selectedAnnouncement.announcement_file}
                                    alt={selectedAnnouncement.title}
                                    style={{ marginBottom: 16 }}
                                />
                            ) : (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        background: "#ffffffff",
                                        padding: "10px 16px",
                                        borderRadius: "8px",
                                        marginBottom: 16,
                                        color: "#f42206ff",
                                    }}
                                >
                                    <FilePdfOutlined style={{ fontSize: 24, marginRight: 8, color: "red" }} />
                                    <a
                                        href={selectedAnnouncement.announcement_file}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: "#ed0a0aff" }}
                                    >
                                        Open & View File
                                    </a>
                                </div>
                            ))}

                        <Paragraph style={{ whiteSpace: "pre-wrap", textAlign: "justify" }}>
                            {selectedAnnouncement.content}
                        </Paragraph>

                        {/* Display Type + Date Range in Modal */}
                        <div style={{ marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 }}>
                            <div>
                                <Text strong style={{ marginRight: 8 }}>Display Type:</Text>
                                {renderDisplayTypeTag(selectedAnnouncement.display_type)}
                            </div>
                            {selectedAnnouncement.display_type === "FLEXIBLE" &&
                                selectedAnnouncement.start_date &&
                                selectedAnnouncement.end_date && (
                                    <div>
                                        <Text strong style={{ marginRight: 8 }}>Date Range:</Text>
                                        <Text type="secondary">
                                            <CalendarOutlined style={{ marginRight: 4 }} />
                                            {dayjs(selectedAnnouncement.start_date).format("DD/MM/YYYY")}
                                            {" → "}
                                            {dayjs(selectedAnnouncement.end_date).format("DD/MM/YYYY")}
                                        </Text>
                                    </div>
                                )}
                        </div>

                        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16 }}>
                            <Text type="secondary">
                                By: {selectedAnnouncement.created_by_username}
                            </Text>
                            <Tag
                                color={
                                    selectedAnnouncement.status === "APPROVED"
                                        ? "green"
                                        : selectedAnnouncement.status === "PENDING"
                                            ? "orange"
                                            : "red"
                                }
                            >
                                {selectedAnnouncement.status}
                            </Tag>
                        </div>
                        <Text type="secondary" style={{ fontSize: "12px" }}>
                            {dayjs(selectedAnnouncement.created_at).format("DD/MM/YYYY HH:mm")}
                        </Text>
                    </div>
                )}

            </Modal>

            <Title
                level={4}
                style={{ textAlign: "center", marginBottom: "20px", color: "#0e497b" }}
            >
                <SoundOutlined style={{ marginRight: 8, color: "#0e497b" }} />
                Announcements
            </Title>

            {/* Filters */}
            {isMobile ? (
                <center>
                    <Space style={{ marginBottom: 24, display: "flex", flexDirection: "column" }}>
                        <Search
                            placeholder="Search by Title"
                            onSearch={(value) => setTitleFilter(value)}
                            onChange={(e) => { setTitleFilter(e.target.value); setCurrentPage(1); }}

                            allowClear
                            style={{ width: 250 }}
                        />
                        <DatePicker
                            placeholder="Filter by Created At"
                            onChange={(date) => { setDateFilter(date); setCurrentPage(1); }}
                            style={{ width: 250 }}
                            allowClear
                        />
                        <Select
                            placeholder="Filter by Display Type"
                            onChange={(value) => {
                                setDisplayTypeFilter(value || "");
                                setCurrentPage(1);
                            }}
                            allowClear
                            style={{ width: 200 }}
                            options={[
                                { label: "Standard", value: "STANDARD" },
                                { label: "Flexible", value: "FLEXIBLE" },
                            ]}
                        />
                    </Space>
                </center>
            ) : (
                <center>
                    <Space style={{ marginBottom: 24 }}>
                        <Search
                            placeholder="Search by Title"
                            onSearch={(value) => setTitleFilter(value)}
                            onChange={(e) => setTitleFilter(e.target.value)}
                            allowClear
                            style={{ width: 250 }}
                        />
                        <DatePicker
                            placeholder="Filter by Created At"
                            onChange={(date) => setDateFilter(date)}
                            style={{ width: 250 }}
                            allowClear
                        />
                        <Select
                            placeholder="Filter by Display Type"
                            onChange={(value) => {
                                setDisplayTypeFilter(value || "");
                                setCurrentPage(1);
                            }}
                            allowClear
                            style={{ width: 200 }}
                            options={[
                                { label: "Standard", value: "STANDARD" },
                                { label: "Flexible", value: "FLEXIBLE" },
                            ]}
                        />
                    </Space>
                </center>
            )}

            <Row gutter={[24, 24]}>
                {paginatedAnnouncements.map((item, index) => {
                    const fileUrl = item.announcement_file;
                    const isLatest = index === 0 && currentPage === 1;

                    const coverContent =
                        fileUrl && isImageFile(fileUrl) ? (
                            <Image
                                alt={item.title}
                                src={fileUrl}
                                height={180}
                                style={{ objectFit: "cover" }}
                                preview={false}
                            />
                        ) : fileUrl ? (
                            <div
                                style={{
                                    height: 180,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "#ffffffff",
                                    borderRadius: "8px",
                                    color: "#ec0b0bff",
                                }}
                            >
                                <FilePdfOutlined style={{ fontSize: 50, color: "red" }} />
                                <a
                                    href={fileUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ marginTop: 8, color: "#ec0b0bff" }}
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    Open & View File
                                </a>
                            </div>
                        ) : (
                            <Image
                                alt={item.title}
                                src={Announcement_image}
                                height={180}
                                style={{ objectFit: "cover" }}
                                preview={false}
                            />
                        );

                    const cardContent = (
                        <Card
                            hoverable
                            onClick={() => handleCardClick(item)}
                            {...(coverContent ? { cover: coverContent } : {})}
                            style={{
                                borderRadius: "12px",
                                overflow: "hidden",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
                                height: 420, // slightly increased to fit new info
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                            }}
                            bodyStyle={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "space-between",
                                flex: 1,
                            }}
                        >
                            <div style={{ flex: 1 }}>
                                {/* Title + Date row — matches Announcements.jsx layout */}
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                    <Text strong ellipsis style={{ fontSize: "14px", maxWidth: "70%" }}>
                                        {item.title}
                                    </Text>
                                    <span style={{ fontSize: "12px", color: "#999", whiteSpace: "nowrap" }}>
                                        <CalendarOutlined style={{ marginRight: 4 }} />
                                        {dayjs(item.created_at).format("DD/MM/YYYY")}
                                    </span>
                                </div>

                                {/* Content — 2-row ellipsis like Announcements.jsx */}
                                <Paragraph
                                    ellipsis={{ rows: 2, expandable: false }}
                                    style={{ marginBottom: 8, color: "#595959", fontSize: "13px" }}
                                >
                                    {item.content}
                                </Paragraph>

                                {/* Display Type + Date Range */}
                                <div style={{ marginBottom: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                    {renderDisplayTypeTag(item.display_type)}
                                    {renderDateRange(item)}
                                </div>
                            </div>
                            <div>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                                    <Text type="secondary">By: {item.created_by_username}</Text>
                                    <Tag
                                        color={
                                            item.status === "APPROVED"
                                                ? "green"
                                                : item.status === "PENDING"
                                                    ? "orange"
                                                    : "red"
                                        }
                                    >
                                        {item.status}
                                    </Tag>
                                </div>
                                <Text type="secondary" style={{ fontSize: "12px" }}>
                                    {dayjs(item.created_at).format("DD/MM/YYYY HH:mm")}
                                </Text>
                            </div>
                        </Card>
                    );

                    return (
                        <Col key={item.id} xs={24} sm={12} lg={8}>
                            {isLatest ? (
                                <Badge.Ribbon text="Latest" color="red">
                                    {cardContent}
                                </Badge.Ribbon>
                            ) : (
                                cardContent
                            )}
                        </Col>
                    );
                })}
            </Row>

            {/* Pagination */}
            {total > 0 && (
                <div style={{ textAlign: "center", marginTop: 24 }}>
                    <Pagination
                        current={currentPage}
                        pageSize={pageSize}
                        total={total}
                        onChange={handlePageChange}
                        showSizeChanger
                        pageSizeOptions={["6", "12", "18", "24"]}
                        showTotal={(total, range) =>
                            `${range[0]}-${range[1]} of ${total} announcements`
                        }
                    />
                </div>
            )}
        </div>
    );
}